// sections/@dashboard/finance/VatSummaryPanel.tsx
// ─── COMPLETE REWRITE ────────────────────────────────────────────────────────
// What's new:
//  1. Quarter selector — user picks Q1/Q2/Q3/Q4 + year, data reloads
//  2. Loading state while VAT summary fetches
//  3. PE adjustment journal button is now wired with feedback
//  4. Download PE working papers button
//  5. VAT100 box values all shown clearly
//  6. "No data" state guides user to import transactions first

import { useEffect, useMemo, useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Box,
  LinearProgress,
  Button,
  Divider,
  Paper,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  AlertTitle,
  Skeleton,
  Tooltip,
  IconButton,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import Iconify from '../../../components/iconify';
import { fCurrency } from '../../../utils/formatNumber';
import { fDate } from '../../../utils/formatTime';
import { pluralize } from '../../../utils/pluralize';
import { IVatSummary, IResidualVatTransactions } from '../../../@types/finance';
import { FinanceService } from '../../../api/finance.service';
import { useSnackbar } from 'notistack';
import { SelectedPeriod } from './FinancePeriodSelector';
import { dateToQuarter, quarterToDateRange } from '../../../utils/vatQuarter';
import { buildVatCsv } from '../../../utils/vatCsv';
import VatOnSalesDialog from './VatOnSalesDialog';

// ── Formatting ─────────────────────────────────────────────────────────────────

/**
 * The shared fCurrency renders zero as an empty string, which left genuine £0
 * figures on this tab (exempt turnover, irrecoverable VAT, …) looking blank.
 * A VAT return has to show the zero.
 */
function fMoney(value: number | null | undefined) {
  return value ? fCurrency(value) : '£0.00';
}

// ── Quarter helpers ────────────────────────────────────────────────────────────

function buildQuarterOptions(fromYear: number, toYear: number) {
  const options: { value: string; label: string }[] = [];
  for (let y = toYear; y >= fromYear; y--) {
    for (let q = 4; q >= 1; q--) {
      options.push({ value: `Q${q}_${y}`, label: `Q${q} ${y}` });
    }
  }
  return options;
}

// ── VAT code config ────────────────────────────────────────────────────────────
const VAT_CODE_CONFIG: Record<
  string,
  { label: string; description: string; color: string; rate: string }
> = {
  S: {
    label: 'Standard rated — 20%',
    description: 'Frames, adult lenses, accessories, contact lenses',
    color: 'primary',
    rate: '20%',
  },
  E: {
    label: 'Exempt',
    description: 'Eye examinations, professional optometry services',
    color: 'success',
    rate: '0% (exempt)',
  },
  Z: {
    label: 'Zero-rated',
    description: "Children's spectacles, eligible contact lenses",
    color: 'info',
    rate: '0% (zero)',
  },
  N: {
    label: 'No VAT',
    description: 'NHS voucher contributions, wages, bank charges',
    color: 'warning',
    rate: 'N/A',
  },
};

// ── Skeleton loader ────────────────────────────────────────────────────────────
function VatSkeleton() {
  return (
    <Stack spacing={2}>
      <Skeleton variant="rounded" height={160} />
      <Grid container spacing={2}>
        {[0, 1, 2, 3].map((i) => (
          <Grid item xs={12} sm={6} key={i}>
            <Skeleton variant="rounded" height={100} />
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}

// ── VAT100 box row ─────────────────────────────────────────────────────────────
function Vat100Row({
  box,
  label,
  value,
  highlight,
  onClick,
  actionHint,
}: {
  box: string;
  label: string;
  value: string;
  highlight?: boolean;
  /** Makes the row a drill-down. */
  onClick?: () => void;
  actionHint?: string;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      sx={{
        ...(onClick && {
          cursor: 'pointer',
          '&:hover, &:focus-visible': { bgcolor: 'action.hover' },
          outline: 'none',
        }),
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'grey.100',
        bgcolor: highlight ? 'primary.lighter' : 'inherit',
        px: highlight ? 1 : 0,
        borderRadius: highlight ? 0.5 : 0,
      }}
    >
      <Typography
        variant="caption"
        fontFamily="monospace"
        fontWeight={700}
        sx={{
          width: 36,
          height: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          borderRadius: 0.5,
          mr: 1.5,
          flexShrink: 0,
        }}
      >
        {box}
      </Typography>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {label}
        {onClick && actionHint && (
          <Typography
            component="span"
            variant="caption"
            color="primary.main"
            sx={{ ml: 1, whiteSpace: 'nowrap' }}
          >
            {actionHint}
          </Typography>
        )}
      </Typography>
      <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
        {value}
      </Typography>
      {onClick && (
        <Iconify
          icon="eva:chevron-right-fill"
          sx={{ ml: 0.5, color: 'text.secondary' }}
        />
      )}
    </Stack>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
interface Props {
  selectedPeriod: SelectedPeriod | null;
}

export default function VatSummaryPanel({ selectedPeriod }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const currentYear = new Date().getFullYear();

  const periodQuarter = useMemo(
    () => (selectedPeriod ? dateToQuarter(selectedPeriod.dateFrom) : null),
    [selectedPeriod],
  );

  const quarterOptions = useMemo(() => {
    const year = selectedPeriod
      ? parseInt(selectedPeriod.dateFrom.split('-')[0], 10)
      : currentYear;
    return buildQuarterOptions(year, year);
  }, [selectedPeriod, currentYear]);

  const [selectedQuarter, setSelectedQuarter] = useState(
    periodQuarter ?? quarterOptions[0]?.value ?? `Q1_${currentYear}`,
  );
  const [vatSummary, setVatSummary] = useState<IVatSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [postingJournal, setPostingJournal] = useState(false);
  const [journalPosted, setJournalPosted] = useState(false);

  // ── Residual VAT drill-down modal ──────────────────────────────────────────
  const [residualOpen, setResidualOpen] = useState(false);
  const [residualLoading, setResidualLoading] = useState(false);
  const [residualData, setResidualData] =
    useState<IResidualVatTransactions | null>(null);

  const handleOpenResidualDrilldown = async () => {
    setResidualOpen(true);
    setResidualLoading(true);
    setResidualData(null);
    try {
      const res = await new FinanceService().getResidualVatTransactions(
        selectedQuarter,
      );
      setResidualData(res.data);
    } catch {
      enqueueSnackbar('Failed to load residual VAT transactions', {
        variant: 'error',
      });
    } finally {
      setResidualLoading(false);
    }
  };

  // ── Box 1 drill-down: items sold and VAT on sales ──────────────────────────
  const [salesOpen, setSalesOpen] = useState(false);
  const salesRange = useMemo(
    () => quarterToDateRange(selectedQuarter),
    [selectedQuarter],
  );

  // ── Sync quarter when the parent period changes ────────────────────────────
  useEffect(() => {
    if (periodQuarter) setSelectedQuarter(periodQuarter);
  }, [periodQuarter]);

  // ── Fetch whenever quarter changes ─────────────────────────────────────────
  // A response for a quarter the user has already moved away from is dropped,
  // so the figures (and the CSV export built from them) always belong to the
  // quarter shown in the selector.
  useEffect(() => {
    if (!selectedQuarter) return undefined;
    let cancelled = false;
    setLoading(true);
    setVatSummary(null);
    new FinanceService()
      .getVatSummary(selectedQuarter)
      .then((res) => {
        if (cancelled) return;
        setVatSummary(res.data);
        setJournalPosted(res.data?.adjustmentJournalPosted ?? false);
      })
      .catch(() => {
        if (!cancelled) {
          enqueueSnackbar('Failed to load VAT summary', { variant: 'error' });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedQuarter, enqueueSnackbar]);

  // ── PE journal ──────────────────────────────────────────────────────────────
  const handlePostJournal = async () => {
    try {
      setPostingJournal(true);
      await new FinanceService().postPeAdjustmentJournal(selectedQuarter);
      enqueueSnackbar('Partial Exemption adjustment journal posted!');
      setJournalPosted(true);
    } catch {
      enqueueSnackbar('Failed to post PE journal', { variant: 'error' });
    } finally {
      setPostingJournal(false);
    }
  };

  // ── VAT100 PDF download ─────────────────────────────────────────────────────
  const handleDownloadVat100 = async () => {
    try {
      const blob = await new FinanceService().generateVat100(selectedQuarter);
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement('a'), {
        href: url,
        download: `VAT100_${selectedQuarter}.pdf`,
      }).click();
      URL.revokeObjectURL(url);
      enqueueSnackbar('VAT100 downloaded!');
    } catch {
      enqueueSnackbar('Failed to generate VAT100', { variant: 'error' });
    }
  };

  // ── Working papers download ─────────────────────────────────────────────────
  const handleDownloadWorkingPapers = async () => {
    try {
      const blob = await new FinanceService().generatePeWorkingPapers(
        selectedQuarter,
      );
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement('a'), {
        href: url,
        download: `PE_Working_Papers_${selectedQuarter}.pdf`,
      }).click();
      URL.revokeObjectURL(url);
      enqueueSnackbar('PE working papers downloaded!');
    } catch {
      enqueueSnackbar('Failed to download working papers', {
        variant: 'error',
      });
    }
  };

  // ── CSV export of this tab, every line stamped with its period ─────────────
  const handleExportCsv = () => {
    if (!vatSummary) return;
    const range = quarterToDateRange(selectedQuarter);
    if (!range) {
      enqueueSnackbar('Pick a valid quarter to export', { variant: 'error' });
      return;
    }
    const csv = buildVatCsv(vatSummary, {
      ...range,
      quarterLabel: vatSummary.quarterLabel,
    });
    // BOM so Excel opens the file as UTF-8 (the quarter label has an en dash).
    const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    Object.assign(document.createElement('a'), {
      href: url,
      download: `VAT_PE_${range.dateFrom}_to_${range.dateTo}.csv`,
    }).click();
    URL.revokeObjectURL(url);
    enqueueSnackbar('VAT & partial exemption CSV exported');
  };

  // ── VAT100 box values — taken straight from the backend single source of ──
  // truth (calculateVatTurnover), so the on-screen boxes always match the PDF.
  const boxes = vatSummary?.vat100;
  const box1 = boxes?.box1OutputTax ?? 0;
  const box3 = boxes?.box3TotalVatDue ?? box1;
  const box4 = boxes?.box4InputTax ?? 0;
  const box5 = boxes?.box5NetVatDue ?? 0;
  const box6 = boxes?.box6TotalSalesExVat ?? 0;
  const box7 = boxes?.box7TotalPurchasesExVat ?? 0;
  const pePercent = ((vatSummary?.peRecoveryRate ?? 0) * 100).toFixed(1);

  return (
    <Stack spacing={2.5}>
      {/* ── Quarter selector ── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1" fontWeight={500}>
          VAT & Partial Exemption
        </Typography>
        <TextField
          select
          size="small"
          label="Quarter"
          value={selectedQuarter}
          onChange={(e) => setSelectedQuarter(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          {quarterOptions.map((q) => (
            <MenuItem key={q.value} value={q.value}>
              {q.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {loading ? (
        <VatSkeleton />
      ) : !vatSummary ? (
        <Alert severity="info" sx={{ borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            No VAT data for {selectedQuarter}
          </Typography>
          <Typography variant="caption">
            Import bank statement transactions and assign categories first.
            Transactions with VAT codes (S, E, Z, N) on their category will
            appear here.
          </Typography>
        </Alert>
      ) : (
        <>
          {/* ── PE calculation card ── */}
          <Card
            sx={{
              background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
              border: '1px solid',
              borderColor: 'secondary.light',
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle1"
                color="secondary.dark"
                gutterBottom
              >
                Partial Exemption Calculation — {vatSummary.quarterLabel}
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {/* Left: figures */}
                <Grid item xs={12} md={7}>
                  {[
                    {
                      label: 'Taxable turnover (S + Z, net of apportionment)',
                      value: fMoney(vatSummary.taxableTurnover),
                    },
                    {
                      label: 'Exempt turnover (E + dispensing service)',
                      value: fMoney(vatSummary.exemptTurnover),
                    },
                    {
                      label: 'NHS / no-VAT income (N coded)',
                      value: fMoney(vatSummary.nhsTurnover ?? 0),
                    },
                    {
                      label: 'Total turnover',
                      value: fMoney(vatSummary.totalTurnover),
                      bold: true,
                    },
                  ].map((row) => (
                    <Stack
                      key={row.label}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        py: 0.75,
                        borderBottom: '1px solid',
                        borderColor: 'secondary.lighter',
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="secondary.dark"
                        fontWeight={row.bold ? 600 : 400}
                      >
                        {row.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        fontWeight={row.bold ? 700 : 500}
                        color="secondary.dark"
                      >
                        {row.value}
                      </Typography>
                    </Stack>
                  ))}

                  {/* ── Input VAT segregation (HMRC Standard Method) ── */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ mt: 1.5, mb: 0.5 }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="secondary.dark"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}
                    >
                      Input VAT (purchases)
                    </Typography>
                    <Tooltip
                      title="Under the HMRC Standard Method the recovery % is applied ONLY to residual (mixed-use) input VAT. Directly attributable input VAT is not apportioned: input VAT attributable to taxable supplies is recovered in full, and input VAT attributable to exempt supplies is not recovered (subject to the de minimis test)."
                      arrow
                    >
                      <Iconify
                        icon="eva:info-outline"
                        width={15}
                        sx={{ color: 'secondary.main', cursor: 'help' }}
                      />
                    </Tooltip>
                  </Stack>

                  {[
                    {
                      label: 'Fully deductible (attributable to taxable)',
                      value: fMoney(vatSummary.fullyDeductibleInputVat),
                      note: 'recovered in full',
                    },
                    {
                      label: 'Attributable to exempt (not recoverable)',
                      value: fMoney(vatSummary.attributableExemptInputVat),
                      note: 'excluded unless de minimis passes',
                    },
                  ].map((row) => (
                    <Stack
                      key={row.label}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        py: 0.75,
                        borderBottom: '1px solid',
                        borderColor: 'secondary.lighter',
                      }}
                    >
                      <Stack>
                        <Typography variant="body2" color="secondary.dark">
                          {row.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ opacity: 0.7 }}
                          color="secondary.dark"
                        >
                          {row.note}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        fontWeight={500}
                        color="secondary.dark"
                      >
                        {row.value}
                      </Typography>
                    </Stack>
                  ))}

                  {/* Residual — clickable drill-down (#2) */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      py: 0.75,
                      borderBottom: '1px solid',
                      borderColor: 'secondary.lighter',
                    }}
                  >
                    <Stack>
                      <Link
                        component="button"
                        type="button"
                        underline="hover"
                        onClick={handleOpenResidualDrilldown}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          color: 'secondary.dark',
                          fontWeight: 600,
                        }}
                      >
                        <Iconify icon="eva:search-fill" width={15} />
                        <Typography variant="body2" fontWeight={600}>
                          Residual input VAT (mixed use)
                        </Typography>
                      </Link>
                      <Typography
                        variant="caption"
                        sx={{ opacity: 0.7 }}
                        color="secondary.dark"
                      >
                        recovery % applied to this only — click to view
                        transactions
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      fontWeight={600}
                      color="secondary.dark"
                    >
                      {fMoney(vatSummary.residualInputVat)}
                    </Typography>
                  </Stack>
                </Grid>

                {/* Apportionment adjustment notice */}
                {(vatSummary as any).apportionmentAdjustment && (
                  <Grid item xs={12}>
                    <Alert
                      severity="info"
                      icon={false}
                      sx={{ borderRadius: 1, py: 0.75 }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        display="block"
                      >
                        §701/57 Apportionment applied to spectacle sales
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fMoney(
                          (vatSummary as any).apportionmentAdjustment
                            .dispenseSplitExempt,
                        )}{' '}
                        of spectacle sale revenue has been reclassified from the
                        taxable (S) bucket to the exempt (E) bucket based on
                        your configured dispensing-fee split. This gives the
                        correct PE recovery rate. Output VAT on taxable element:{' '}
                        {fMoney(
                          (vatSummary as any).apportionmentAdjustment
                            .dispenseOutputVat,
                        )}
                        .
                      </Typography>
                    </Alert>
                  </Grid>
                )}

                {/* Right: PE rate highlight */}
                <Grid item xs={12} md={5}>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: 'secondary.main',
                      color: 'white',
                      borderRadius: 1.5,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      gap: 0.75,
                    }}
                  >
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      PE recovery rate
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.6, fontSize: 11 }}
                    >
                      Taxable ÷ Total × 100 — applied to residual VAT only
                    </Typography>
                    <Typography
                      variant="h2"
                      fontFamily="monospace"
                      fontWeight={700}
                      noWrap
                      title={`${pePercent}%`}
                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {pePercent}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={parseFloat(pePercent)}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        '& .MuiLinearProgress-bar': { bgcolor: 'white' },
                        borderRadius: 1,
                        my: 0.5,
                      }}
                    />
                    <Divider
                      sx={{ borderColor: 'rgba(255,255,255,0.2)', my: 0.5 }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.7, fontSize: 11 }}
                    >
                      Fully deductible:{' '}
                      {fMoney(vatSummary.fullyDeductibleInputVat)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.7, fontSize: 11 }}
                    >
                      + Residual recovered ({pePercent}%):{' '}
                      {fMoney(vatSummary.recoverableResidualVat)}
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{ mt: 0.5 }}
                    >
                      Total recoverable VAT:{' '}
                      {fMoney(vatSummary.totalRecoverableVat)}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Irrecoverable: {fMoney(vatSummary.irrecoverableVat)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* ── De minimis test conclusion (#3) ── */}
              {vatSummary.deMinimis && (
                <Alert
                  severity={vatSummary.deMinimis.passed ? 'success' : 'warning'}
                  icon={
                    <Iconify
                      icon={
                        vatSummary.deMinimis.passed
                          ? 'eva:checkmark-circle-2-fill'
                          : 'eva:alert-triangle-fill'
                      }
                    />
                  }
                  sx={{ mt: 2, borderRadius: 1 }}
                >
                  <AlertTitle sx={{ fontWeight: 700, mb: 0.5 }}>
                    {vatSummary.deMinimis.conclusion}
                  </AlertTitle>
                  <Typography variant="caption" display="block">
                    Exempt input tax for the period:{' '}
                    <strong>
                      {fMoney(vatSummary.deMinimis.exemptInputTax)}
                    </strong>
                    .
                  </Typography>
                  <Typography variant="caption" display="block">
                    Test 1 — ≤ £{vatSummary.deMinimis.monthlyLimit}/month (
                    {fMoney(vatSummary.deMinimis.limit)} over{' '}
                    {vatSummary.deMinimis.monthsInPeriod} months,{' '}
                    {vatSummary.deMinimis.vatPeriod} scheme):{' '}
                    <strong>
                      {vatSummary.deMinimis.passesMonthlyLimit
                        ? 'PASS'
                        : 'FAIL'}
                    </strong>
                  </Typography>
                  <Typography variant="caption" display="block">
                    Test 2 — ≤ 50% of total input tax (
                    {fMoney(vatSummary.deMinimis.halfTotalInputTax)}):{' '}
                    <strong>
                      {vatSummary.deMinimis.passesHalfTest ? 'PASS' : 'FAIL'}
                    </strong>
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    {vatSummary.deMinimis.passed
                      ? 'Both tests pass, so ALL input VAT (including the exempt element) is recoverable this period.'
                      : 'A test fails, so exempt input tax is not recoverable and the recovery % is applied to residual VAT.'}
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* ── VAT code breakdown cards ── */}
          <Typography variant="subtitle2">Turnover by VAT code</Typography>
          <Grid container spacing={2}>
            {(vatSummary.breakdown ?? []).map((item) => {
              const config = VAT_CODE_CONFIG[item.code] ?? {
                label: item.code,
                description: '',
                color: 'info',
                rate: '',
              };
              const pct =
                vatSummary.totalTurnover > 0
                  ? Math.round(
                      (item.totalValue / vatSummary.totalTurnover) * 100,
                    )
                  : 0;

              return (
                <Grid item xs={12} sm={6} key={item.code}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      mb={1.5}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: `${config.color}.lighter`,
                          color: `${config.color}.main`,
                          fontWeight: 700,
                          fontFamily: 'monospace',
                          fontSize: 15,
                        }}
                      >
                        {item.code}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2">
                          {config.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {config.description}
                        </Typography>
                      </Box>
                      <Box sx={{ ml: 'auto' }}>
                        <Typography
                          variant="caption"
                          sx={{
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 0.75,
                            bgcolor: `${config.color}.lighter`,
                            color: `${config.color}.main`,
                            fontWeight: 600,
                          }}
                        >
                          {config.rate}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mb: 0.75 }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {pluralize(item.transactionCount, 'transaction')}
                      </Typography>
                      <Typography
                        variant="caption"
                        fontFamily="monospace"
                        fontWeight={600}
                      >
                        {fMoney(item.totalValue)}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      color={config.color as any}
                      sx={{ height: 6, borderRadius: 1 }}
                    />
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ mt: 0.5, display: 'block' }}
                    >
                      {pct}% of total turnover
                      {item.vatAmount > 0 &&
                        ` · VAT: ${fMoney(item.vatAmount)}`}
                    </Typography>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* ── VAT100 boxes ── */}
          <Card variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              VAT100 return values — {vatSummary.quarterLabel}
            </Typography>
            <Vat100Row
              box="1"
              label="VAT due on sales and other outputs (post-apportionment)"
              value={fMoney(box1)}
              onClick={salesRange ? () => setSalesOpen(true) : undefined}
              actionHint="View items sold & VAT"
            />
            <Vat100Row
              box="2"
              label="VAT due on EC acquisitions"
              value="£0.00"
            />
            <Vat100Row
              box="3"
              label="Total VAT due (Box 1 + Box 2)"
              value={fMoney(box3)}
            />
            <Vat100Row
              box="4"
              label="VAT reclaimed on purchases (input tax)"
              value={fMoney(box4)}
              highlight
            />
            <Vat100Row
              box="5"
              label={
                box5 >= 0
                  ? 'Net VAT payable to HMRC (Box 3 − Box 4)'
                  : 'Net VAT reclaimable from HMRC (Box 3 − Box 4)'
              }
              value={fMoney(Math.abs(box5))}
              highlight
            />
            <Vat100Row
              box="6"
              label="Total value of sales and other outputs (ex. VAT)"
              value={fMoney(box6)}
            />
            <Vat100Row
              box="7"
              label="Total value of purchases and other inputs (ex. VAT)"
              value={fMoney(box7)}
            />
            <Vat100Row box="8" label="Total value of EC sales" value="£0.00" />
            <Vat100Row
              box="9"
              label="Total value of EC acquisitions"
              value="£0.00"
            />
          </Card>

          {/* ── Actions ── */}
          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:file-text-fill" />}
              onClick={handleDownloadVat100}
            >
              Generate VAT100
            </Button>
            <Button
              variant="outlined"
              startIcon={<Iconify icon="eva:file-outline" />}
              onClick={handleExportCsv}
            >
              Export CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<Iconify icon="eva:download-fill" />}
              onClick={handleDownloadWorkingPapers}
            >
              Export PE working papers
            </Button>
            <Button
              variant="outlined"
              color={journalPosted ? 'success' : 'secondary'}
              disabled={journalPosted || postingJournal}
              startIcon={
                postingJournal ? (
                  <CircularProgress size={14} color="inherit" />
                ) : journalPosted ? (
                  <Iconify icon="eva:checkmark-circle-2-fill" />
                ) : (
                  <Iconify icon="eva:plus-fill" />
                )
              }
              onClick={handlePostJournal}
            >
              {postingJournal
                ? 'Posting…'
                : journalPosted
                  ? 'Adjustment journal posted'
                  : 'Post PE adjustment journal'}
            </Button>
          </Stack>
        </>
      )}

      {/* ── Box 1 drill-down: items sold & VAT on sales ── */}
      {salesRange && vatSummary && (
        <VatOnSalesDialog
          open={salesOpen}
          onClose={() => setSalesOpen(false)}
          dateFrom={salesRange.dateFrom}
          dateTo={salesRange.dateTo}
          quarterLabel={vatSummary.quarterLabel}
          box1={box1}
          incomeSources={vatSummary.incomeSources}
        />
      )}

      {/* ── Residual VAT drill-down modal (#2) ── */}
      <Dialog
        open={residualOpen}
        onClose={() => setResidualOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          Residual input VAT — audit trail
          {residualData && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {residualData.quarterLabel} ·{' '}
              {pluralize(residualData.count, 'transaction')} · total residual
              VAT {fMoney(residualData.totalResidualVat)}
            </Typography>
          )}
          <IconButton
            onClick={() => setResidualOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {residualLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !residualData || residualData.transactions.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 1 }}>
              No residual/mixed-use standard-rated expenses found for this
              period.
            </Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Net</TableCell>
                  <TableCell align="right">Gross</TableCell>
                  <TableCell align="right">Residual VAT</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {residualData.transactions.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell>
                      <Typography variant="caption">
                        {/* Older API builds send a full ISO timestamp. */}
                        {fDate(String(t.date).slice(0, 10), 'dd/MM/yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{t.supplier}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {t.category}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontFamily="monospace">
                        {fMoney(t.net)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontFamily="monospace">
                        {fMoney(t.gross)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        fontWeight={600}
                      >
                        {fMoney(t.residualVat)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={5} align="right">
                    <Typography variant="body2" fontWeight={700}>
                      Total residual input VAT
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      fontWeight={700}
                    >
                      {fMoney(residualData.totalResidualVat)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
